import { useTranslation } from "react-i18next";
import type { SessionSubdata } from "../../types/sessionTypes";
import PeopleAIDetail from "./interactions/PeopleAIDetail";
import DebriefingAIDetail from "./interactions/DebriefingAIDetail";
import Quiz3Detail from "./interactions/Quiz3Detail";
import QuizSortDetail from "./interactions/QuizSortDetail";
import SpeechToCheckDetail from "./interactions/SpeechToCheckDetail";
import ZoneDetectDetail from "./interactions/ZoneDetectDetail";
import YesNoQuestionDetail from "./interactions/YesNoQuestionDetail";
import DangerCatchDetail from "./interactions/DangerCatchDetail";
import PhotoCatchDetail from "./interactions/PhotoCatchDetail";
import QuizImageMultipleDetail from "./interactions/QuizImageMultipleDetail";
import RandomQuizzDetail from "./interactions/RandomQuizzDetail";
import ZoneSortDetail from "./interactions/ZoneSortDetail";

interface InteractionContentRendererProps {
  interaction: SessionSubdata;
  onLlmLoaded?: (llm: string) => void;
}

/**
 * Renders the appropriate interaction detail component based on content type.
 * Extracted from SessionDetailModal to improve maintainability.
 */
const InteractionContentRenderer = ({
  interaction,
  onLlmLoaded,
}: InteractionContentRendererProps) => {
  const { t } = useTranslation();

  // Parse specific_data if it's a string (JSON)
  let specificData = interaction.specific_data || {};
  if (typeof specificData === "string") {
    try {
      specificData = JSON.parse(specificData);
    } catch (e) {
      console.error("Failed to parse specific_data:", e);
      specificData = {};
    }
  }

  // Get content type from the raw data (before formatting)
  const contentType = (interaction.rawContentType || "").toLowerCase().replace(/-/g, "_");

  switch (contentType) {
    case "content_avatar_ai_persona":
    case "avatar_ai_persona":
    case "people_ai":
    case "peopleai":
      return (
        <PeopleAIDetail
          data={specificData}
          onLlmLoaded={onLlmLoaded}
          score={interaction.score}
          scoreMax={interaction.scoreMax}
        />
      );

    case "content_avatar_ai_session_debrief":
    case "avatar_ai_session_debrief":
    case "debriefing_ai":
    case "debriefingai":
      return <DebriefingAIDetail data={specificData} />;

    case "content_random_quizz":
    case "random_quizz":
    case "random_quiz":
    case "randomquiz":
      return (
        <RandomQuizzDetail
          data={{ ...specificData, score: interaction.score }}
          contentId={interaction.content?.id}
        />
      );

    case "content_quizz_3":
    case "quizz_3":
      return (
        <Quiz3Detail
          data={{ ...specificData, score: interaction.score }}
          contentId={interaction.content?.id}
        />
      );

    case "content_quizz_sort":
    case "quizz_sort":
    case "quiz_sort":
    case "quizsort":
      return <QuizSortDetail data={specificData} contentId={interaction.content?.id} />;

    case "content_speech_to_check":
    case "speech_to_check":
    case "speechtocheck":
      return <SpeechToCheckDetail data={specificData} contentId={interaction.content?.id} />;

    case "content_zone_detect":
    case "zone_detect":
    case "zonedetect":
      return <ZoneDetectDetail data={specificData} contentId={interaction.content?.id} />;

    case "content_zone_sort":
    case "zone_sort":
    case "zonesort":
      return <ZoneSortDetail data={specificData} contentId={interaction.content?.id} />;

    case "content_yes_no_question":
    case "yes_no_question":
    case "yesnoquestion":
      return <YesNoQuestionDetail score={interaction.score} contentId={interaction.content?.id} />;

    case "content_danger_catch":
    case "danger_catch":
    case "dangercatch":
      return <DangerCatchDetail score={interaction.score} contentId={interaction.content?.id} />;

    case "content_photo_catch":
    case "photo_catch":
    case "photocatch":
      return <PhotoCatchDetail score={interaction.score} contentId={interaction.content?.id} />;

    case "content_quizz_image_multiple":
    case "quizz_image_multiple":
    case "quizzimagemultiple":
      return <QuizImageMultipleDetail data={specificData} contentId={interaction.content?.id} />;

    default:
      return (
        <div className="h-full flex flex-col items-center justify-center text-center p-8">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg
              className="h-8 w-8 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t("statsNew.interaction.comingSoon")}
          </h3>
          <p className="text-sm text-gray-500 max-w-sm">
            {t("statsNew.interaction.comingSoonDescription")}
          </p>
        </div>
      );
  }
};

export default InteractionContentRenderer;
